import { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PropRow, PropSection } from "../style";
import ColorPicker from "./ColorPicker";
import useLanguage from "../../../hooks/useLanguage";

const FONTS = ["Heebo", "Assistant", "Rubik", "NotoSansHebrew"];
const WEIGHTS = [400, 700];
const ALIGNS = ["start", "center", "end"];

function NumField({ label, value, onChange, min, max, step = 1 }) {
  return (
    <TextField
      label={label}
      size="small"
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(Number(e.target.value))}
      inputProps={{ min, max, step }}
      sx={{ flex: 1 }}
    />
  );
}

function CommonProps({ el, dispatch, tp }) {
  const set = (key) => (val) => dispatch({ type: "UPDATE", id: el.id, patch: { [key]: val } });

  return (
    <PropSection>
      <Typography variant="caption" color="text.secondary">{tp.position || "Position & Size"}</Typography>
      <PropRow>
        <NumField label="X" value={el.x} onChange={set("x")} />
        <NumField label="Y" value={el.y} onChange={set("y")} />
      </PropRow>
      <PropRow>
        <NumField label="W" value={el.w} onChange={set("w")} min={1} />
        <NumField label="H" value={el.h} onChange={set("h")} min={1} />
      </PropRow>
      <PropRow>
        <NumField label={tp.rotation || "Rotation"} value={el.rotation ?? 0} onChange={set("rotation")} min={-360} max={360} />
        <NumField
          label={tp.opacity || "Opacity (%)"}
          value={Math.round((el.opacity ?? 1) * 100)}
          onChange={(v) => set("opacity")(Math.max(0, Math.min(100, Number(v) || 0)) / 100)}
          min={0}
          max={100}
          step={1}
        />
      </PropRow>
      <PropRow>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{tp.zOrder || "Z-order"}</Typography>
        <Typography variant="caption">{el.z}</Typography>
      </PropRow>
    </PropSection>
  );
}

function TextProps({ el, dispatch, tt }) {
  const set = (key) => (val) => dispatch({ type: "UPDATE", id: el.id, patch: { [key]: val } });

  const setShadow = (key) => (val) =>
    dispatch({ type: "UPDATE", id: el.id, patch: { shadow: { ...(el.shadow || {}), [key]: val } } });

  const setOutline = (key) => (val) =>
    dispatch({ type: "UPDATE", id: el.id, patch: { outline: { ...(el.outline || {}), [key]: val } } });

  return (
    <PropSection>
      <Divider />
      <Typography variant="caption" color="text.secondary">{tt.header || "Text"}</Typography>
      <TextField
        label={tt.label || "Label"}
        size="small"
        value={el.label || ""}
        onChange={(e) => set("label")(e.target.value)}
        inputProps={{ dir: "auto" }}
      />
      <TextField
        label={tt.defaultText || "Default Text"}
        size="small"
        value={el.defaultText || ""}
        onChange={(e) => set("defaultText")(e.target.value)}
        multiline
        rows={2}
        inputProps={{ dir: "auto" }}
      />
      <FormControl size="small" fullWidth>
        <InputLabel>{tt.font || "Font"}</InputLabel>
        <Select label={tt.font || "Font"} value={el.font || "Heebo"} onChange={(e) => set("font")(e.target.value)}>
          {FONTS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
        </Select>
      </FormControl>
      <PropRow>
        <NumField label={tt.size || "Size (px)"} value={el.size || 48} onChange={set("size")} min={8} />
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>{tt.weight || "Weight"}</InputLabel>
          <Select label={tt.weight || "Weight"} value={el.weight || 400} onChange={(e) => set("weight")(Number(e.target.value))}>
            {WEIGHTS.map((w) => <MenuItem key={w} value={w}>{w === 700 ? (tt.weightBold || "Bold") : (tt.weightNormal || "Normal")}</MenuItem>)}
          </Select>
        </FormControl>
      </PropRow>
      <FormControl size="small" fullWidth>
        <InputLabel>{tt.align || "Align"}</InputLabel>
        <Select label={tt.align || "Align"} value={el.align || "start"} onChange={(e) => set("align")(e.target.value)}>
          {ALIGNS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </Select>
      </FormControl>
      <PropRow sx={{ alignItems: "center" }}>
        <Typography variant="caption" sx={{ flex: 1 }}>{tt.color || "Color"}</Typography>
        <ColorPicker value={el.color || "#ffffff"} onChange={set("color")} />
      </PropRow>

      <Divider />
      <Typography variant="caption" color="text.secondary">{tt.shadow || "Shadow"}</Typography>
      <PropRow>
        <ColorPicker label={tt.color || "Color"} value={el.shadow?.color || "#000000"} onChange={setShadow("color")} />
      </PropRow>
      <PropRow>
        <NumField label={tt.shadowOffsetX || "Offset X"} value={el.shadow?.offsetX ?? 0} onChange={setShadow("offsetX")} />
        <NumField label={tt.shadowOffsetY || "Offset Y"} value={el.shadow?.offsetY ?? 0} onChange={setShadow("offsetY")} />
        <NumField label={tt.shadowBlur || "Blur"} value={el.shadow?.blur ?? 0} onChange={setShadow("blur")} min={0} />
      </PropRow>

      <Divider />
      <Typography variant="caption" color="text.secondary">{tt.outline || "Outline"}</Typography>
      <PropRow>
        <ColorPicker label={tt.color || "Color"} value={el.outline?.color || "#000000"} onChange={setOutline("color")} />
        <NumField label={tt.outlineWidth || "Width"} value={el.outline?.width ?? 0} onChange={setOutline("width")} min={0} />
      </PropRow>
    </PropSection>
  );
}

const LOGO_FIT_MAX = 300;

function readImageDimensions(srcOrFile) {
  return new Promise((resolve, reject) => {
    const isFile = typeof srcOrFile !== "string";
    const url = isFile ? URL.createObjectURL(srcOrFile) : srcOrFile;
    const img = new window.Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      if (isFile) URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = (err) => {
      if (isFile) URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function fitToMax(dims, max = LOGO_FIT_MAX) {
  if (!dims || !dims.width || !dims.height) return null;
  if (dims.width >= dims.height) {
    return { w: max, h: Math.round(max * (dims.height / dims.width)) };
  }
  return { h: max, w: Math.round(max * (dims.width / dims.height)) };
}

function LogoProps({ el, dispatch, uploadLogo, ensureTemplate, tlogo }) {
  const [uploading, setUploading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const dims = await readImageDimensions(file).catch(() => null);
      const id = await ensureTemplate();
      if (!id) return;
      const result = await uploadLogo(id, el.id, file);
      const fit = fitToMax(dims);
      dispatch({
        type: "UPDATE",
        id: el.id,
        patch: {
          s3Key: result.s3Key,
          filename: result.filename || file.name,
          _previewUrl: result.url,
          ...(fit || {}),
        },
      });
    } catch {
      // error handled in hook
    } finally {
      setUploading(false);
    }
  };

  const handleResetAspect = async () => {
    if (!el._previewUrl) return;
    setResetting(true);
    try {
      const dims = await readImageDimensions(el._previewUrl);
      const fit = fitToMax(dims);
      if (fit) dispatch({ type: "UPDATE", id: el.id, patch: fit });
    } catch {
      // ignore
    } finally {
      setResetting(false);
    }
  };

  return (
    <PropSection>
      <Divider />
      <Typography variant="caption" color="text.secondary">{tlogo.header || "Logo (locked to template — only text is dynamic)"}</Typography>
      <Button
        variant="outlined"
        size="small"
        component="label"
        disabled={uploading}
        startIcon={uploading ? <CircularProgress size={14} /> : null}
      >
        {uploading ? (tlogo.uploading || "Uploading…") : (el.s3Key ? (tlogo.replace || "Replace Logo") : (tlogo.upload || "Upload Logo"))}
        <input type="file" hidden accept="image/*" onChange={handleFile} />
      </Button>
      {el._previewUrl && (
        <>
          <Box component="img" src={el._previewUrl} alt="logo preview" sx={{ width: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "borderColor.default" }} />
          <Button
            size="small"
            variant="text"
            disabled={resetting}
            onClick={handleResetAspect}
          >
            {resetting ? (tlogo.resetting || "Resetting…") : (tlogo.resetAspect || "Reset to natural aspect ratio")}
          </Button>
        </>
      )}
      {el.filename && (
        <Typography variant="caption" color="text.secondary">{el.filename}</Typography>
      )}
    </PropSection>
  );
}

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) || 0;
  const g = parseInt(h.slice(2, 4), 16) || 0;
  const b = parseInt(h.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${alpha ?? 1})`;
}

function buildCssGradient(stops) {
  if (!stops || stops.length < 2) return "#888";
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const parts = sorted.map((s) => `${hexToRgba(s.color, s.alpha ?? 1)} ${Math.round(s.position * 100)}%`);
  return `linear-gradient(90deg, ${parts.join(", ")})`;
}

function GradientStopsBar({ stops, onStopsChange, tgStop = {} }) {
  const barRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const canRemove = stops.length > 2;

  const getPositionFromEvent = (e) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  };

  const handleBarMouseDown = (e) => {
    if (e.target !== barRef.current) return;
    const pos = getPositionFromEvent(e);
    onStopsChange([...stops, { color: "#ffffff", position: pos, alpha: 1 }]);
  };

  const handleHandleMouseDown = (e, idx) => {
    e.stopPropagation();
    const startX = e.clientX;
    const target = e.currentTarget;
    let moved = false;

    const onMove = (mv) => {
      if (!moved && Math.abs(mv.clientX - startX) < 3) return;
      moved = true;
      const pos = getPositionFromEvent(mv);
      const next = stops.map((s, i) => (i === idx ? { ...s, position: pos } : s));
      onStopsChange(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!moved) {
        setActiveIdx(idx);
        setAnchorEl(target);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const updateStop = (key, val) => {
    if (activeIdx === null) return;
    const next = stops.map((s, i) => (i === activeIdx ? { ...s, [key]: val } : s));
    onStopsChange(next);
  };

  const removeActive = () => {
    if (!canRemove || activeIdx === null) return;
    onStopsChange(stops.filter((_, i) => i !== activeIdx));
    setAnchorEl(null);
    setActiveIdx(null);
  };

  const activeStop = activeIdx !== null ? stops[activeIdx] : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        ref={barRef}
        onMouseDown={handleBarMouseDown}
        sx={{
          position: "relative",
          width: "100%",
          height: 32,
          borderRadius: 1,
          backgroundImage: `${buildCssGradient(stops)}, linear-gradient(45deg, #999 25%, transparent 25%, transparent 75%, #999 75%), linear-gradient(45deg, #999 25%, transparent 25%, transparent 75%, #999 75%)`,
          backgroundSize: "100% 100%, 12px 12px, 12px 12px",
          backgroundPosition: "0 0, 0 0, 6px 6px",
          cursor: "crosshair",
          border: "1px solid",
          borderColor: "borderColor.dark",
        }}
      >
        {stops.map((stop, idx) => (
          <Tooltip key={idx} title={`${stop.color} · ${Math.round((stop.alpha ?? 1) * 100)}%`}>
            <Box
              onMouseDown={(e) => handleHandleMouseDown(e, idx)}
              sx={{
                position: "absolute",
                left: `${stop.position * 100}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: stop.color,
                border: "2px solid #fff",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.3)",
                cursor: "grab",
                zIndex: 1,
                transition: "transform 0.1s",
                "&:hover": { transform: "translate(-50%, -50%) scale(1.2)" },
                "&:active": { cursor: "grabbing" },
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setActiveIdx(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        marginThreshold={12}
        slotProps={{
          paper: {
            sx: { width: 280, maxWidth: "calc(100vw - 24px)", overflow: "visible", mt: 1 },
          },
        }}
      >
        {activeStop && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <PropRow sx={{ alignItems: "center" }}>
              <Typography variant="caption" sx={{ flex: 1 }}>{tgStop.color || "Color"}</Typography>
              <ColorPicker value={activeStop.color} onChange={(v) => updateStop("color", v)} />
            </PropRow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 48 }}>{tgStop.alpha || "Alpha"}</Typography>
              <Slider
                size="small"
                value={activeStop.alpha ?? 1}
                min={0} max={1} step={0.01}
                onChange={(_, v) => updateStop("alpha", v)}
                sx={{ flex: 1 }}
              />
              <Typography variant="caption" sx={{ width: 36, textAlign: "right" }}>{Math.round((activeStop.alpha ?? 1) * 100)}%</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 48 }}>{tgStop.position || "Position"}</Typography>
              <Slider
                size="small"
                value={activeStop.position ?? 0}
                min={0} max={1} step={0.01}
                onChange={(_, v) => updateStop("position", v)}
                sx={{ flex: 1 }}
              />
              <Typography variant="caption" sx={{ width: 32, textAlign: "right" }}>{Math.round((activeStop.position ?? 0) * 100)}%</Typography>
            </Box>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={!canRemove}
              onClick={removeActive}
              fullWidth
            >
              {tgStop.remove || "Remove"}
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
}

const DIRECTION_PRESETS = [
  { deg: 225, icon: "↖", row: 0, col: 0 },
  { deg: 270, icon: "↑", row: 0, col: 1 },
  { deg: 315, icon: "↗", row: 0, col: 2 },
  { deg: 180, icon: "←", row: 1, col: 0 },
  { deg: 0,   icon: "→", row: 1, col: 2 },
  { deg: 135, icon: "↙", row: 2, col: 0 },
  { deg: 90,  icon: "↓", row: 2, col: 1 },
  { deg: 45,  icon: "↘", row: 2, col: 2 },
];

function DirectionPicker({ value, onChange }) {
  const current = ((value ?? 0) % 360 + 360) % 360;
  // Min angular distance, then "within ±22.5° of deg" means active.
  const isActive = (deg) => {
    let d = Math.abs(current - deg) % 360;
    if (d > 180) d = 360 - d;
    return d < 22.5;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "repeat(3, 1fr)",
        gap: 0.5,
        width: 108,
        height: 108,
        p: 0.5,
        bgcolor: "background.default",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "borderColor.default",
      }}
    >
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => {
          const preset = DIRECTION_PRESETS.find((p) => p.row === row && p.col === col);
          if (!preset) {
            return (
              <Box
                key={`c-${row}-${col}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "text.disabled",
                }}
              >
                {Math.round(current)}°
              </Box>
            );
          }
          const active = isActive(preset.deg);
          return (
            <Box
              key={`b-${row}-${col}`}
              onClick={() => onChange(preset.deg)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: 0.5,
                fontSize: 18,
                lineHeight: 1,
                color: active ? "primary.contrastText" : "text.primary",
                bgcolor: active ? "action.main" : "transparent",
                border: "1px solid",
                borderColor: active ? "action.main" : "borderColor.default",
                userSelect: "none",
                "&:hover": { bgcolor: active ? "action.main" : "action.hover" },
              }}
            >
              {preset.icon}
            </Box>
          );
        })
      )}
    </Box>
  );
}

function GradientProps({ el, dispatch, tg }) {
  const set = (key) => (val) => dispatch({ type: "UPDATE", id: el.id, patch: { [key]: val } });

  return (
    <PropSection>
      <Divider />
      <Typography variant="caption" color="text.secondary">{tg.header || "Gradient"}</Typography>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <DirectionPicker value={el.direction ?? 90} onChange={set("direction")} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">{tg.direction || "Direction"}</Typography>
          <TextField
            size="small"
            type="number"
            value={el.direction ?? 90}
            onChange={(e) => set("direction")(Number(e.target.value))}
            inputProps={{ min: 0, max: 360, step: 5 }}
            InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>°</Typography> }}
          />
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">{tg.stops || "Stops — click bar to add, click handle to edit"}</Typography>
      <GradientStopsBar stops={el.stops || []} onStopsChange={set("stops")} tgStop={tg} />
    </PropSection>
  );
}

function ShapeProps({ el, dispatch, ts }) {
  const set = (key) => (val) => dispatch({ type: "UPDATE", id: el.id, patch: { [key]: val } });

  return (
    <PropSection>
      <Divider />
      <Typography variant="caption" color="text.secondary">{ts.header || "Shape"}</Typography>
      <PropRow>
        <Typography variant="caption" sx={{ flex: 1 }}>{ts.fill || "Fill"}</Typography>
        <ColorPicker value={el.fill || "#000000"} onChange={set("fill")} />
      </PropRow>
      <PropRow>
        <Typography variant="caption" sx={{ flex: 1 }}>{ts.alpha || "Alpha"}</Typography>
        <Slider size="small" value={el.alpha ?? 1} min={0} max={1} step={0.01} onChange={(_, v) => set("alpha")(v)} sx={{ flex: 2 }} />
        <Typography variant="caption" sx={{ width: 36, textAlign: "right" }}>{Math.round((el.alpha ?? 1) * 100)}%</Typography>
      </PropRow>
      <NumField label={ts.radius || "Corner Radius (px)"} value={el.radius ?? 0} onChange={set("radius")} min={0} />
    </PropSection>
  );
}

export default function PropertiesPanel({ element, dispatch, uploadLogo, ensureTemplate }) {
  const [{ pages }] = useLanguage();
  const tOT = pages.overlayTemplates || {};
  const tp = tOT.props || {};
  const tt = tOT.text || {};
  const tlogo = tOT.logo || {};
  const tg = tOT.gradient || {};
  const ts = tOT.shape || {};

  if (!element) {
    return (
      <Box sx={{ p: 2, color: "text.secondary" }}>
        <Typography variant="body2">{tp.selectPrompt || "Select an element to edit its properties."}</Typography>
      </Box>
    );
  }

  const typeLabel = (tp.typeProperties || "{type} properties").replace("{type}", element.type);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
        {typeLabel}
      </Typography>
      <CommonProps el={element} dispatch={dispatch} tp={tp} />
      {element.type === "text" && <TextProps el={element} dispatch={dispatch} tt={tt} />}
      {element.type === "logo" && <LogoProps el={element} dispatch={dispatch} uploadLogo={uploadLogo} ensureTemplate={ensureTemplate} tlogo={tlogo} />}
      {element.type === "gradient" && <GradientProps el={element} dispatch={dispatch} tg={tg} />}
      {element.type === "shape" && <ShapeProps el={element} dispatch={dispatch} ts={ts} />}
    </Box>
  );
}
